import styled from 'styled-components';
import React, {FC} from 'react';

import {palette, fontWeights, transition} from '../../../../lib/styleVariables';
import {Icon} from '../../../molecules/icon';
import {HeaderMenuLeftSubItemProps} from './headerMenuTypes';
import {paletteR4, fontSizesMixinsMobileR4} from '../../../../lib/styleVariablesR4';
import {hasHref} from './headerUtils';

const StyledExpandedMobilMenuItemWrapper = styled.div`
  border-bottom: 1px solid ${paletteR4.dark_blue.base};
`;

const StyledMainItemWrapper = styled.div``;

const StyledSubMenuWrapper = styled.div`
  padding: 24px 0 48px;
`;

const StyledIcon = styled(Icon)`
  color: inherit;
  align-self: center;
  position: absolute;
  right: 5px;
  top: 50%;
  transform: translate(0, -50%);

  * {
    fill: none;
  }
`;

export const StyledMenuItem = styled.a`
  ${fontSizesMixinsMobileR4.h4()};
  line-height: 160%;
  display: block;
  padding: 16px 0;
  text-decoration: none;
  position: relative;
  color: ${paletteR4.black.base};

  [data-theme='dark'] & {
    color: ${palette.white.base};
  }
`;

const StyledCategory = styled.div`
  &:not(:first-child) {
    margin-top: 40px;
    padding-top: 40px;
    border-top: 1px solid ${paletteR4.dark_blue.base};
  }
`;

const StyledCategoryTitle = styled.h5`
  ${fontSizesMixinsMobileR4.h6()};
  font-weight: ${fontWeights.bold};
  color: ${paletteR4.red.lowlight};
  text-transform: uppercase;
  margin: 0;
`;

const StyledSubMenuItemTitle = styled.h6<{isActive: boolean}>`
  ${fontSizesMixinsMobileR4.h4()};
  font-weight: ${fontWeights.semibold};
  margin-top: 0;
  margin-bottom: 4px;
  transition: ${transition.fast};
  color: ${props => (props.isActive ? paletteR4.blue.base : paletteR4.black.base)};

  [data-theme='dark'] & {
    color: ${props => (props.isActive ? paletteR4.blue.base : paletteR4.white.base)};
  }
`;

const StyledSubMenuItem = styled.a`
  text-decoration: none;
  display: flex;

  &:not(:first-child) {
    margin-top: 40px;
  }
`;

const StyledSubMenuItemIcon = styled(Icon)`
  margin-right: 24px;
  flex-shrink: 0;
`;

const StyledSubMenuItemDesc = styled.p`
  ${fontSizesMixinsMobileR4.p3()};
  color: ${paletteR4.black.base};
  margin: 0;

  [data-theme='dark'] & {
    color: ${paletteR4.white.base};
  }
`;

export interface MobileHeaderMenuPrimaryItemProps {
  name: string;
  subMenu?: {
    items: Array<HeaderMenuLeftSubItemProps>;
  };
  href?: string;
}

interface MobileHeaderMenuPrimaryProps {
  item: MobileHeaderMenuPrimaryItemProps;
  locationPath: string;
}

export const MobileHeaderMenuPrimaryItem: FC<MobileHeaderMenuPrimaryProps> = props => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const hasSubItem = !hasHref(props.item);

  return (
    <StyledExpandedMobilMenuItemWrapper>
      <StyledMainItemWrapper
        onClick={() => {
          if (hasSubItem)
            setIsExpanded(!isExpanded);
        }}
      >
        <StyledMenuItem href={hasSubItem ? undefined : props.item.href}>
          {props.item.name}
          {hasSubItem && (
            <StyledIcon name={isExpanded ? 'chevronUp' : 'chevronDown'} color={palette.black.base} useR4 />
          )}
        </StyledMenuItem>
      </StyledMainItemWrapper>
      {isExpanded && (
        <StyledSubMenuWrapper>
          {props.item?.subMenu?.items.map((category: any) => (
            <StyledCategory key={category.name}>
              <StyledCategoryTitle>{category.name}</StyledCategoryTitle>
              {category.items.map((item: any) => (
                <StyledSubMenuItem href={item.href}>
                  {item.iconName && <StyledSubMenuItemIcon useR4 name={item.iconName} size={40} />}
                  <div>
                    <StyledSubMenuItemTitle isActive={item.href === props.locationPath}>
                      {item.name}
                    </StyledSubMenuItemTitle>
                    <StyledSubMenuItemDesc>{item.description}</StyledSubMenuItemDesc>
                  </div>
                </StyledSubMenuItem>
              ))}
            </StyledCategory>
          ))}
        </StyledSubMenuWrapper>
      )}
    </StyledExpandedMobilMenuItemWrapper>
  );
};
